import { useEffect } from "react";
import { useDragDropManager } from "react-dnd";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  emailBuilderCloseBlockSettings,
  emailBuilderCloseInlineContent,
  emailBuilderSetMode,
} from "redux/actions/emailBuilder/emailBuilder";
import type { TEmailBuilderActivePanel } from "redux/reducers/emailBuilder/emailBuilder";
import {
  resolveEmailBuilderDesignEscapeAction,
  shouldIgnoreEmailBuilderDesignEscapeTarget,
} from "../emailBuilderDesignEscapeKey";
import { tryDismissEmailBuilderDesignEscapeOverlay } from "../emailBuilderDesignEscapeOverlay";
import { releaseEmailBuilderCanvasKeyboardFocus } from "../canvas/settings/emailBuilderSettingsFocus";
import { useEmailBuilderViewport } from "../emailBuilderViewportContext";
import { focusAndRevealSelection } from "../../domain/reveal-selection";
import { useEditorActions, useEditorStore } from "../../domain/redux/hooks";
import { selectSelection, selectTextEditingBlock } from "../../domain/selectors";
import { selectionEntityId } from "../../domain/selection/entity-id";

export function useEmailBuilderDesignEscapeKey(args: {
  mode: "design" | "preview";
  activePanel: TEmailBuilderActivePanel;
  mobileToolbarOpen: boolean;
  dismissToolbarPanel: () => void;
  disabled?: boolean;
}): void {
  const { mode, activePanel, mobileToolbarOpen, dismissToolbarPanel, disabled = false } = args;
  const dispatch = useAppDispatch();
  const { usesBottomSheetChrome } = useEmailBuilderViewport();
  const editingMode = useAppSelector(store => store.emailBuilderStore.editingMode);
  const textEditingBlock = useEditorStore(selectTextEditingBlock);
  const selection = useEditorStore(selectSelection);
  const { setSelection, setTextEditingBlock } = useEditorActions();
  const hasCanvasSelection = selectionEntityId(selection) !== null;
  const dndManager = useDragDropManager();

  useEffect(() => {
    if (disabled || (mode !== "design" && mode !== "preview")) {
      return;
    }

    function handleKeydown(event: KeyboardEvent) {
      if (event.key !== "Escape" || event.defaultPrevented) {
        return;
      }

      if (shouldIgnoreEmailBuilderDesignEscapeTarget(event.target)) {
        return;
      }

      if (mode === "preview") {
        if (tryDismissEmailBuilderDesignEscapeOverlay()) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        dispatch(emailBuilderSetMode("design"));
        return;
      }

      if (dndManager.getMonitor().isDragging()) {
        return;
      }

      if (tryDismissEmailBuilderDesignEscapeOverlay()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const action = resolveEmailBuilderDesignEscapeAction({
        editingMode: textEditingBlock !== null ? "inline-content" : editingMode,
        activePanel,
        usesBottomSheetChrome,
        mobileToolbarOpen,
        hasCanvasSelection,
      });

      if (action === "none") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      switch (action) {
        case "close-inline-content": {
          const editingTarget = textEditingBlock;
          setTextEditingBlock(null);
          if (globalThis.document.activeElement instanceof HTMLElement) {
            globalThis.document.activeElement.blur();
          }
          if (editingTarget !== null) {
            const blockSelection = {
              kind: "block" as const,
              rowId: editingTarget.rowId,
              columnId: editingTarget.columnId,
              blockId: editingTarget.blockId,
            };
            setSelection(blockSelection);
            focusAndRevealSelection(blockSelection, { pulse: false });
          }
          dispatch(emailBuilderCloseInlineContent());
          return;
        }
        case "close-chrome":
          dismissToolbarPanel();
          dispatch(emailBuilderCloseBlockSettings());
          setSelection({ kind: "email" });
          releaseEmailBuilderCanvasKeyboardFocus();
          return;
        case "close-toolbar":
          dismissToolbarPanel();
          return;
        case "close-settings":
          dispatch(emailBuilderCloseBlockSettings());
          return;
        case "clear-blocks":
          dispatch(emailBuilderCloseBlockSettings());
          setSelection({ kind: "email" });
          releaseEmailBuilderCanvasKeyboardFocus();
          return;
        default:
          return;
      }
    }

    window.document.addEventListener("keydown", handleKeydown, true);
    return () => {
      window.document.removeEventListener("keydown", handleKeydown, true);
    };
  }, [
    activePanel,
    disabled,
    dispatch,
    dismissToolbarPanel,
    dndManager,
    editingMode,
    hasCanvasSelection,
    mobileToolbarOpen,
    mode,
    setSelection,
    setTextEditingBlock,
    textEditingBlock,
    usesBottomSheetChrome,
  ]);
}
